import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  Download, 
  Share2, 
  Check, 
  Palette, 
  Layout, 
  Image as ImageIcon,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Loader2,
  Sparkles,
  Building2,
  Calendar,
  Users,
  Heart,
  Smile,
  Eye,
  ChevronDown,
  AlertTriangle
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn, formatDateSafe } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import {
  DEFAULT_QUOTE_CONTRIBUTION,
  normalizeQuoteContribution,
  type ProposalTemplate,
  type ColorScheme,
  type ProposalSettings,
  type QuoteContributionConfig,
} from "../types";

interface Step9Props {
  downloadPreviewPDF: (reportType?: string, contributionConfig?: QuoteContributionConfig) => Promise<void>;
}

const DEFAULT_PROPOSAL_SETTINGS: ProposalSettings = {
  template: "modern",
  colorScheme: "blue",
  showLogo: true,
  showBenefitsDetail: true,
  showEmployeeBreakdown: true,
  coverLetter: "",
};

const PDF_REPORT_OPTIONS = [
  { id: "group_proposal", label: "Group Proposal", description: "Full branded proposal with benefits grid and employee rates" },
  { id: "executive_summary", label: "Executive Summary", description: "High-level overview for decision makers" },
  { id: "detailed_proposal", label: "Detailed Proposal", description: "Complete plan details with all benefits" },
  { id: "rate_comparison", label: "Rate Comparison", description: "Side-by-side plan rate comparison" },
  { id: "employee_census", label: "Employee Census", description: "Employee-level premium breakdown" },
  { id: "contribution_analysis", label: "Contribution Analysis", description: "Employer/employee cost split details" },
  { id: "renewal_comparison", label: "Renewal Comparison", description: "Current vs proposed benefits comparison" },
];

const getCoverageIcon = (coverageType: string) => {
  const type = coverageType.toLowerCase();
  switch (type) {
    case 'health':
    case 'medical':
      return Heart;
    case 'dental':
      return Smile;
    case 'vision':
      return Eye;
    default:
      return Heart;
  }
};

const TEMPLATES: { id: ProposalTemplate; name: string; description: string; preview: string }[] = [
  { id: "modern", name: "Modern", description: "Clean, contemporary design with bold typography", preview: "bg-gradient-to-br from-blue-500 to-indigo-600" },
  { id: "classic", name: "Classic", description: "Traditional, professional layout", preview: "bg-gradient-to-br from-slate-600 to-slate-800" },
  { id: "minimal", name: "Minimal", description: "Simple, content-focused design", preview: "bg-gradient-to-br from-gray-100 to-gray-300" },
  { id: "corporate", name: "Corporate", description: "Formal, business-oriented style", preview: "bg-gradient-to-br from-emerald-600 to-teal-700" },
];

const COLOR_SCHEMES: { id: ColorScheme; name: string; primary: string; accent: string }[] = [
  { id: "blue", name: "Ocean Blue", primary: "bg-blue-600", accent: "bg-blue-100" },
  { id: "green", name: "Forest Green", primary: "bg-emerald-600", accent: "bg-emerald-100" },
  { id: "purple", name: "Royal Purple", primary: "bg-purple-600", accent: "bg-purple-100" },
  { id: "orange", name: "Sunset Orange", primary: "bg-orange-600", accent: "bg-orange-100" },
  { id: "slate", name: "Executive Gray", primary: "bg-slate-600", accent: "bg-slate-100" },
];

export function Step9GenerateQuote({ downloadPreviewPDF }: Step9Props) {
  const { quoteData, premiumSummary, currentPremiumSummary, draftQuoteId, updateQuoteData } = useQuoteWizardContext();
  const { toast } = useToast();
  
  const [settings, setSettings] = useState<ProposalSettings>(
    quoteData.proposalSettings || DEFAULT_PROPOSAL_SETTINGS
  );
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Employer contribution strategy lives on the quote (persisted with the draft
  // and sent with the share link) so the client-facing proposal starts from
  // the broker's numbers instead of a hardcoded default.
  const [contribution, setContribution] = useState<QuoteContributionConfig>(
    quoteData.contribution ? normalizeQuoteContribution(quoteData.contribution) : { ...DEFAULT_QUOTE_CONTRIBUTION },
  );

  useEffect(() => {
    updateQuoteData({ proposalSettings: settings });
  }, [settings]);

  useEffect(() => {
    updateQuoteData({ contribution });
  }, [contribution]);

  const totalPlans = Object.values(quoteData.selectedPlans).flat().length;
  const totalEmployees = quoteData.selectedEmployees.length;

  // Gates Generate/Export so they can't fire mid-calculation. A wait-loop
  // inside the handlers wouldn't work — premiumSummary/currentPremiumSummary
  // are closure values from the render that created the handler, so polling
  // them there just re-checks the same stale snapshot forever.
  const premiumCalcLoading = premiumSummary.loading || currentPremiumSummary.loading;
  // DEV-382: plans the totals had to leave out (no rates for this quote -
  // typically none for the group's rating area). Sending a quote with them
  // silently missing (or at $0) is what this blocks.
  const unpricedPlans = [
    ...(premiumSummary.unpricedPlans ?? []),
    ...(currentPremiumSummary.unpricedPlans ?? []),
  ].filter((u, i, all) => all.findIndex((x) => x.planId === u.planId) === i);
  const hasUnpricedPlans = unpricedPlans.length > 0;
  
  const premiumsByCoverageType = (() => {
    const result: Record<string, { min: number; max: number; label: string }> = {};
    if (premiumSummary.planBreakdowns) {
      premiumSummary.planBreakdowns.forEach((b: any) => {
        const premium = b.totalMonthlyPremium || 0;
        if (premium > 0) {
          const coverageType = b.coverageType || 'other';
          if (!result[coverageType]) {
            result[coverageType] = { 
              min: premium, 
              max: premium,
              label: coverageType.charAt(0).toUpperCase() + coverageType.slice(1)
            };
          } else {
            result[coverageType].min = Math.min(result[coverageType].min, premium);
            result[coverageType].max = Math.max(result[coverageType].max, premium);
          }
        }
      });
    }
    return result;
  })();
  
  const handleGenerateShareLink = async () => {
    if (!draftQuoteId) {
      toast({
        title: "No quote found",
        description: "Please save the quote first before generating a share link.",
        variant: "destructive"
      });
      return;
    }
    // Button is disabled while loading; guard here too for the one render
    // where that's still flipping.
    if (hasUnpricedPlans) {
      toast({
        title: "Some plans can't be priced",
        description: unpricedPlans.map((u) => u.reason).join(" "),
        variant: "destructive"
      });
      return;
    }
    if (premiumCalcLoading) {
      toast({
        title: "Still calculating",
        description: "Premiums are still being calculated — try again in a moment.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      // First, save the current premiumSummary to the quote (planBreakdowns contains full plan data)
      // Use estimated values (one plan per coverage type) for primary totals, not sum of ALL options
      await apiRequest("PUT", `/api/quotes/${draftQuoteId}`, {
        wizardState: {
          contribution,
          proposalSettings: settings,
          premiumSummary: {
            totalMonthlyPremium: premiumSummary.estimatedMonthlyPremium || 0,
            totalAnnualPremium: premiumSummary.estimatedAnnualPremium || 0,
            totalEmployeePortion: premiumSummary.estimatedEmployeePortion || 0,
            totalEmployerPortion: premiumSummary.estimatedEmployerPortion || 0,
            planBreakdowns: premiumSummary.planBreakdowns || [],
            byCoverageType: premiumSummary.byCoverageType || {},
            premiumRangeMin: premiumSummary.premiumRangeMin || 0,
            premiumRangeMax: premiumSummary.premiumRangeMax || 0,
          },
          // Same snapshot, for the current plan — without this the public
          // share-link page has no premium to show for it, even though
          // currentPlans itself is already saved separately.
          currentPremiumSummary: {
            totalMonthlyPremium: currentPremiumSummary.estimatedMonthlyPremium || 0,
            totalAnnualPremium: currentPremiumSummary.estimatedAnnualPremium || 0,
            totalEmployeePortion: currentPremiumSummary.estimatedEmployeePortion || 0,
            totalEmployerPortion: currentPremiumSummary.estimatedEmployerPortion || 0,
            planBreakdowns: currentPremiumSummary.planBreakdowns || [],
            byCoverageType: currentPremiumSummary.byCoverageType || {},
            premiumRangeMin: currentPremiumSummary.premiumRangeMin || 0,
            premiumRangeMax: currentPremiumSummary.premiumRangeMax || 0,
          },
        },
        totalMonthlyPremium: premiumSummary.estimatedMonthlyPremium || null,
        totalAnnualPremium: premiumSummary.estimatedAnnualPremium || null,
      });
      
      // Then generate the share link
      const response = await apiRequest("POST", `/api/quotes/${draftQuoteId}/share`, {
        proposalSettings: settings,
      });
      const data = await response.json();
      const baseUrl = window.location.origin;
      setShareLink(`${baseUrl}/quote/${data.shareToken}`);
      toast({ title: "Share link generated!", description: "You can now share this link with your client." });
    } catch (error) {
      console.error("Failed to generate share link:", error);
      toast({ 
        title: "Failed to generate link", 
        description: "Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      await navigator.clipboard.writeText(shareLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      toast({ title: "Copied!", description: "Link copied to clipboard." });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-medium">Final Step</span>
        </div>
        <h3 className="text-2xl font-bold mb-2">Create Your Proposal</h3>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Customize the look and feel of your proposal before sharing it with your client
        </p>
      </div>

      {hasUnpricedPlans && !premiumCalcLoading && (
        <Alert variant="destructive" data-testid="alert-unpriced-plans">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {unpricedPlans.length === 1 ? "1 selected plan can't be priced" : `${unpricedPlans.length} selected plans can't be priced`}
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1">
              {unpricedPlans.map((u) => (
                <li key={u.planId}>{u.reason}</li>
              ))}
            </ul>
            <div className="mt-2">
              These plans are left out of the totals, so the share link is blocked until they're fixed or removed.
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Quote Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">{quoteData.selectedEntity?.name || "Client"}</div>
                <div className="text-xs text-muted-foreground">{quoteData.clientType}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">
                  {quoteData.effectiveDate ? formatDateSafe(quoteData.effectiveDate) : "Not set"}
                </div>
                <div className="text-xs text-muted-foreground">Effective Date</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">{totalEmployees} {pluralize(totalEmployees, "Employee")}</div>
                <div className="text-xs text-muted-foreground">Enrolled in benefits</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Heart className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium">{totalPlans} {pluralize(totalPlans, "Plan")}</div>
                <div className="text-xs text-muted-foreground">Selected for proposal</div>
              </div>
            </div>

            {Object.keys(premiumsByCoverageType).length > 0 && (
              <div className="pt-3 border-t space-y-2">
                {Object.entries(premiumsByCoverageType).map(([coverageType, data]) => {
                  const Icon = getCoverageIcon(coverageType);
                  return (
                    <div key={coverageType} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                      <Icon className="h-4 w-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-muted-foreground">{data.label}</div>
                        <div className="text-sm font-bold text-primary">
                          {data.min === data.max ? (
                            `$${data.max.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                          ) : (
                            `$${data.min.toLocaleString('en-US', { minimumFractionDigits: 2 })} - $${data.max.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                          )}
                          <span className="text-xs font-normal text-muted-foreground ml-1">/mo</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              Proposal Design
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="template" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="template" className="gap-2">
                  <Layout className="h-4 w-4" />
                  Template
                </TabsTrigger>
                <TabsTrigger value="colors" className="gap-2">
                  <Palette className="h-4 w-4" />
                  Colors
                </TabsTrigger>
                <TabsTrigger value="content" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Content
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="template" className="mt-4">
                <div className="grid grid-cols-2 gap-3">
                  {TEMPLATES.map((template) => (
                    <div
                      key={template.id}
                      className={cn(
                        "relative cursor-pointer rounded-lg border-2 p-3 transition-all hover:shadow-md",
                        settings.template === template.id
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent bg-muted/30 hover:border-muted-foreground/20"
                      )}
                      onClick={() => setSettings({ ...settings, template: template.id })}
                    >
                      <div className={cn("h-20 rounded-md mb-2", template.preview)} />
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-muted-foreground">{template.description}</div>
                      {settings.template === template.id && (
                        <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="colors" className="mt-4">
                <div className="grid grid-cols-5 gap-3">
                  {COLOR_SCHEMES.map((scheme) => (
                    <div
                      key={scheme.id}
                      className={cn(
                        "cursor-pointer rounded-lg border-2 p-3 text-center transition-all hover:shadow-md",
                        settings.colorScheme === scheme.id
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent bg-muted/30"
                      )}
                      onClick={() => setSettings({ ...settings, colorScheme: scheme.id })}
                    >
                      <div className="flex justify-center gap-1 mb-2">
                        <div className={cn("h-6 w-6 rounded-full", scheme.primary)} />
                        <div className={cn("h-6 w-6 rounded-full", scheme.accent)} />
                      </div>
                      <div className="text-xs font-medium">{scheme.name}</div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="content" className="mt-4 space-y-4">
                <div className="space-y-3">
                  <Label 
                    htmlFor="coverLetter" 
                    className="text-sm font-medium flex items-center gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Cover Letter Message (Optional)
                  </Label>
                  <textarea
                    id="coverLetter"
                    className="w-full min-h-[100px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add a personalized message for your client..."
                    value={settings.coverLetter}
                    onChange={(e) => setSettings({ ...settings, coverLetter: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Display Options</Label>
                  <div className="space-y-2">
                    {[
                      { key: "showLogo", label: "Show company logo" },
                      { key: "showBenefitsDetail", label: "Include detailed benefits breakdown" },
                      { key: "showEmployeeBreakdown", label: "Show employee-level rates" },
                    ].map((option) => (
                      <label
                        key={option.key}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-muted-foreground/20 text-primary focus:ring-primary"
                          checked={settings[option.key as keyof ProposalSettings] as boolean}
                          onChange={(e) =>
                            setSettings({ ...settings, [option.key]: e.target.checked })
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Employer Contribution Strategy */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Employer Contribution Strategy
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Employer share of the employee's own premium and of the dependents' premium. Used in the shared
            proposal and exported reports.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={contribution.type === "percentage" ? "default" : "outline"}
              size="sm"
              onClick={() => setContribution((p) => ({ ...p, type: "percentage" }))}
            >
              Percentage
            </Button>
            <Button
              variant={contribution.type === "fixed" ? "default" : "outline"}
              size="sm"
              onClick={() => setContribution((p) => ({ ...p, type: "fixed" }))}
            >
              Fixed Amount
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm">Employee</Label>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  max={contribution.type === "percentage" ? 100 : undefined}
                  value={contribution.employee}
                  onChange={(e) =>
                    setContribution((p) => ({ ...p, employee: parseFloat(e.target.value) || 0 }))
                  }
                  className="pr-8"
                  data-testid="contribution-employee"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {contribution.type === "percentage" ? "%" : "$"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {contribution.type === "percentage" ? "of the employee's premium" : "per employee per month"}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Dependents</Label>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  max={contribution.type === "percentage" ? 100 : undefined}
                  value={contribution.dependent}
                  onChange={(e) =>
                    setContribution((p) => ({ ...p, dependent: parseFloat(e.target.value) || 0 }))
                  }
                  className="pr-8"
                  data-testid="contribution-dependent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {contribution.type === "percentage" ? "%" : "$"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {contribution.type === "percentage" ? "of the dependents' premium" : "toward dependents per month"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Share2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-1">Share with Client</h4>
                <p className="text-sm text-muted-foreground max-w-md">
                  Generate a secure link to share this proposal with your client. They'll see benefits, rates, and employee details in a professional format.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              {shareLink ? (
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-background rounded-md border">
                    <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      readOnly
                      value={shareLink}
                      className="border-0 p-0 h-auto text-sm focus-visible:ring-0"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      {copySuccess ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={shareLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" disabled={premiumCalcLoading}>
                        {premiumCalcLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        {premiumCalcLoading ? "Calculating..." : "Export Reports"}
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuItem onClick={() => downloadPreviewPDF(undefined, contribution)}>
                        <FileText className="h-4 w-4 mr-2" />
                        <div>
                          <div className="font-medium">Standard Proposal</div>
                          <div className="text-xs text-muted-foreground">Complete quote document</div>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {PDF_REPORT_OPTIONS.map((option) => (
                        <DropdownMenuItem
                          key={option.id}
                          onClick={() => downloadPreviewPDF(option.id, contribution)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-xs text-muted-foreground">{option.description}</div>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    onClick={handleGenerateShareLink}
                    disabled={isGenerating || premiumCalcLoading || hasUnpricedPlans}
                    title={hasUnpricedPlans ? "Some selected plans can't be priced — see the warning above" : undefined}
                  >
                    {isGenerating || premiumCalcLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <LinkIcon className="h-4 w-4 mr-2" />
                    )}
                    {premiumCalcLoading && !isGenerating ? "Calculating..." : "Generate Share Link"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
